import React, {
    useReducer, useImperativeHandle, forwardRef, useState, useEffect,
} from 'react';
import { makeStyles } from '@material-ui/core';
import clsx from 'clsx';
import PropTypes from 'prop-types';
import update from 'immutability-helper';
import StringUtils from 'lib/StringUtils';
import FullCalendar from '@fullcalendar/react';
import dayGridPlugin from '@fullcalendar/daygrid';
import timeGridPlugin from '@fullcalendar/timegrid';
import listWeek from '@fullcalendar/list';
import Toolbar from 'components/widgets/calendar/Toolbar';
import ActivitiesForm from 'components/modules/crm/activities/form/ActivitiesForm';
import ServiceAppointmentForm from 'components/modules/service/ServiceAppointmentForm';
import AvatarWithLabel from 'components/widgets/AvatarWithLabel';
import { MEDIA_QUERY } from 'utils/enum/Core';

const NAV_GROUP = {
    NEXT: 'NEXT',
    PREVIOUS: 'PREV',
    GO_TODAY: 'TODAY',
};

const VIEW_GROUP = {
    MONTH: {
        value: 'MONTH',
        view: 'dayGridMonth',
    },
    WEEK: {
        value: 'WEEK',
        view: 'timeGridWeek',
    },
    DAY: {
        value: 'DAY',
        view: 'dayGridWeek',
    },
    LIST_WEEK: {
        value: 'LIST_WEEK',
        view: 'listWeek',
    },
};
const initState = {
    currentDate: new Date(),
    selectedView: VIEW_GROUP.MONTH,
    selectedServiceRecord: null,
    record: {
        crmId: null,
        activityId: null,
        type: null,
    },
    openDialog: false,
};
const ACTION_TYPES = {
    SET_RECORD: 'setRecord',
    SET_SERVICE_RECORD: 'setServiceRecord',
    SET_CURRENT_DATE: 'setCurrentDate',
    ON_CHANGE_VIEW_GROUP: 'onChangeViewGroup',
    TOGGLE_FORM: 'toggleForm',
};
const reducer = (state, action) => {
    switch (action.type) {
    case ACTION_TYPES.SET_CURRENT_DATE: {
        return update(state, {
            currentDate: { $set: action.payload },
        });
    }
    case ACTION_TYPES.ON_CHANGE_VIEW_GROUP: {
        const record = action.payload;
        return update(state, {
            currentDate: { $set: record.date },
            selectedView: { $set: record.selectedView },
        });
    }
    case ACTION_TYPES.SET_RECORD:
        const { opportunity, activityId, type } = action.payload;
        return update(state, {
            record: {
                activityId: { $set: activityId },
                crmId: { $set: opportunity.crmId },
                lotId: { $set: opportunity.lotId },
                type: { $set: type.toUpperCase() },
            },
            openDialog: { $set: true },
        });
    case ACTION_TYPES.SET_SERVICE_RECORD:
        const record = action.payload;
        return update(state, {
            selectedServiceRecord: { $set: record },
            openDialog: { $set: true },
        });
    case ACTION_TYPES.TOGGLE_FORM: {
        return update(state, {
            ...(state.openDialog ? {
                selectedServiceRecord: { $set: null },
                record: {
                    type: { $set: null },
                    crmId: { $set: null },
                    lotId: { $set: null },
                    activityId: { $set: null },
                },
            } : {}),
            openDialog: { $set: !state.openDialog },
        });
    }
    default:
        return state;
    }
};

const useStyles = makeStyles(() => ({
    text: {
        fontSize: '12px',
        position: 'relative',
        [`@media (max-width: ${MEDIA_QUERY.W_430}px)`]: {
            fontSize: '10px',
        },
    },
    eventTitle: {
        display: 'flex',
        alignItems: 'center',
    },
    avatar: {
        position: 'absolute',
        top: '10px',
        right: '-10px',
        [`@media (max-width: ${MEDIA_QUERY.W_430}px)`]: {
            top: '2px',
            right: '-6px',
            '& > div > div': {
                width: '20px',
                height: '20px',
            },
        },
    },
    avatarMonth: {
        top: '2px',
        right: '-14px',
        position: 'absolute',
        '& > div > div': {
            width: '22px',
            height: '22px',
        },
        [`@media (max-width: ${MEDIA_QUERY.W_430}px)`]: {
            display: 'none',
        },
    },
    calendarContainer: {
        height: '700px',
        [`@media (max-width: ${MEDIA_QUERY.W_430}px)`]: {
            height: '500px',
        },
        '& .fc': {
            [`@media (max-width: ${MEDIA_QUERY.W_430}px)`]: {
                fontSize: '0.75rem',
            },
        },
        '& .fc-toolbar-title': {
            [`@media (max-width: ${MEDIA_QUERY.W_430}px)`]: {
                fontSize: '1rem',
            },
        },
        '& .fc-col-header-cell-cushion': {
            [`@media (max-width: ${MEDIA_QUERY.W_430}px)`]: {
                padding: '2px',
            },
        },
        '& .fc-daygrid-day-number': {
            [`@media (max-width: ${MEDIA_QUERY.W_430}px)`]: {
                fontSize: '0.75rem',
            },
        },
        '& .fc-timegrid-slot-label': {
            [`@media (max-width: ${MEDIA_QUERY.W_430}px)`]: {
                fontSize: '0.7rem',
            },
        },
    },
}));

const MainPanel = forwardRef(({
    updateDateRange,
    records,
    isService,
    advisors,
    refetchServiceAppointments,
    onToggleDrawer,
}, ref) => {
    const classes = useStyles();
    const calendarRef = React.createRef();
    const [state, dispatch] = useReducer(reducer, {
        ...initState,
        ...(isService ? {
            selectedView: VIEW_GROUP.WEEK,
        } : {}),
    });

    const [isMobile, setIsMobile] = useState(window.innerWidth <= MEDIA_QUERY.W_430);
    const [calendarHeight, setCalendarHeight] = useState(() => {
        if (window.innerWidth <= MEDIA_QUERY.W_430) return 500;
        if (window.innerWidth <= MEDIA_QUERY.W_992) return 600;
        return 700;
    });

    useEffect(() => {
        const handleResize = () => {
            const width = window.innerWidth;
            setIsMobile(width <= MEDIA_QUERY.W_430);
            if (width <= MEDIA_QUERY.W_430) {
                setCalendarHeight(500);
            } else if (width <= MEDIA_QUERY.W_992) {
                setCalendarHeight(600);
            } else {
                setCalendarHeight(700);
            }
        };

        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    const toggleAppointmentForm = () => {
        dispatch({
            type: ACTION_TYPES.TOGGLE_FORM,
        });
    };

    useImperativeHandle(ref, () => ({
        onOpenForm() {
            toggleAppointmentForm();
        },
    }));

    const onClickEvent = (info) => {
        const { record } = info.event.extendedProps;
        dispatch({
            type: isService ? ACTION_TYPES.SET_SERVICE_RECORD : ACTION_TYPES.SET_RECORD,
            payload: record,
        });
    };

    const onClickNavigationGroup = (action) => {
        const calendarApi = calendarRef.current.getApi();

        switch (action) {
        case NAV_GROUP.NEXT:
            calendarApi.next();
            break;
        case NAV_GROUP.PREVIOUS:
            calendarApi.prev();
            break;
        case NAV_GROUP.GO_TODAY:
            calendarApi.today();
            break;
        default:
            calendarApi.today();
            break;
        }

        const date = calendarApi.getDate();
        dispatch({
            type: ACTION_TYPES.SET_CURRENT_DATE,
            payload: date,
        });
        updateDateRange(date, state.selectedView.value.toLowerCase());
    };

    const onClickViewGroup = (newView) => {
        const calendarApi = calendarRef.current.getApi();
        calendarApi.changeView(newView.view);
        const date = calendarApi.getDate();

        updateDateRange(date, newView.value.toLowerCase());
        dispatch({
            type: ACTION_TYPES.ON_CHANGE_VIEW_GROUP,
            payload: {
                date,
                selectedView: newView,
            },
        });
    };

    return (
        <>
            <Toolbar
                currentDate={state.currentDate}
                currentView={state.selectedView}
                onClickNavigationGroup={onClickNavigationGroup}
                onClickViewGroup={onClickViewGroup}
                onToggleDrawer={onToggleDrawer}
            />
            <div className={classes.calendarContainer}>
                <FullCalendar
                    ref={calendarRef}
                    headerToolbar={false}
                    events={records}
                    height={calendarHeight}
                    eventContent={(arg) => {
                        const { extendedProps } = arg.event;
                        const { record } = extendedProps ?? {};
                        const isMonth = state.selectedView.value === VIEW_GROUP.MONTH.value;

                        if (record) {
                            const {
                                advisorFullName,
                                advisorPhoto,
                            } = record;

                            return (
                                <div className={clsx(classes.text, 'fc-event-main-frame')}>
                                    <div className="fc-event-time">
                                        {arg.timeText}
                                    </div>
                                    <div className={clsx(classes.eventTitle, 'fc-event-title-container')}>
                                        <div className="fc-event-title fc-sticky text-ellipsis">
                                            {StringUtils.toPascalCase((arg.event._def.title ?? '').toLowerCase())}
                                        </div>
                                    </div>
                                    {isService && !isMobile && (
                                        <div className={isMonth ? classes.avatarMonth : classes.avatar}>
                                            <AvatarWithLabel
                                                onlyPhoto
                                                person={{
                                                    fullName: advisorFullName,
                                                    picture: advisorPhoto,
                                                }}
                                            />
                                        </div>
                                    )}
                                </div>
                            );
                        }

                        return null;
                    }}
                    initialView={isService ? VIEW_GROUP.WEEK.view : VIEW_GROUP.MONTH.view}
                    plugins={[dayGridPlugin, timeGridPlugin, listWeek]}
                    eventClick={onClickEvent}
                />
            </div>
            {state.openDialog && !isService && (
                <ActivitiesForm
                    isEditing
                    onClose={toggleAppointmentForm}
                    open={state.openDialog}
                    type={state.record.type}
                    crmId={state.record.crmId}
                    lotId={state.record.lotId}
                    activityId={state.record.activityId}
                />
            )}
            {state.openDialog && isService && (
                <ServiceAppointmentForm
                    onClose={toggleAppointmentForm}
                    advisors={advisors}
                    selectedServiceRecord={state.selectedServiceRecord}
                    refetchServiceAppointments={refetchServiceAppointments}
                />
            )}
        </>
    );
});

MainPanel.defaultProps = {
    advisors: [],
    refetchServiceAppointments: () => null,
    onToggleDrawer: () => null,
};

MainPanel.propTypes = {
    updateDateRange: PropTypes.func.isRequired,
    records: PropTypes.array.isRequired,
    isService: PropTypes.bool.isRequired,
    advisors: PropTypes.array,
    refetchServiceAppointments: PropTypes.func,
    onToggleDrawer: PropTypes.func,
};

export default MainPanel;
